import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useCloudSave } from '@/arcade'
import { DEFAULT_SAVE, MAX_LIVES, migrateSave, type TakeMeHomeSave } from '@/types/save'

interface ProgressContextValue {
  save: TakeMeHomeSave
  loaded: boolean
  update: (fn: (prev: TakeMeHomeSave) => TakeMeHomeSave) => void
  addCoins: (n: number) => void
  addGems: (n: number) => void
  spendCoins: (n: number) => boolean
  spendGems: (n: number) => boolean
  consumeLife: () => boolean
}

const ProgressContext = createContext<ProgressContextValue | null>(null)

const LIFE_REGEN_MS = 5 * 60 * 1000

export function ProgressProvider({ children }: { children: ReactNode }) {
  const { value: rawValue, setValue, loaded } = useCloudSave<TakeMeHomeSave>(DEFAULT_SAVE, 'main')
  const value = useMemo(() => migrateSave(rawValue), [rawValue])

  const update = useCallback(
    (fn: (prev: TakeMeHomeSave) => TakeMeHomeSave) => {
      setValue((prev) => {
        const base = migrateSave(prev ?? DEFAULT_SAVE)
        const next = fn(base)
        return { ...next, updatedAt: Date.now() }
      })
    },
    [setValue],
  )

  useEffect(() => {
    if (!loaded) return
    const tick = () => {
      setValue((prev) => {
        const base = migrateSave(prev ?? DEFAULT_SAVE)
        if (base.economy.lives >= MAX_LIVES) return base
        const elapsed = Date.now() - base.economy.livesUpdatedAt
        const gained = Math.floor(elapsed / LIFE_REGEN_MS)
        if (gained <= 0) return base
        const lives = Math.min(MAX_LIVES, base.economy.lives + gained)
        const remainder = elapsed - gained * LIFE_REGEN_MS
        const livesUpdatedAt = lives >= MAX_LIVES ? Date.now() : Date.now() - remainder
        return { ...base, economy: { ...base.economy, lives, livesUpdatedAt }, updatedAt: Date.now() }
      })
    }
    tick()
    const id = window.setInterval(tick, 15000)
    return () => window.clearInterval(id)
  }, [loaded, setValue])

  const addCoins = useCallback(
    (n: number) => update((s) => ({ ...s, economy: { ...s.economy, coins: s.economy.coins + n } })),
    [update],
  )
  const addGems = useCallback(
    (n: number) => update((s) => ({ ...s, economy: { ...s.economy, gems: s.economy.gems + n } })),
    [update],
  )
  const spendCoins = useCallback(
    (n: number) => {
      if (value.economy.coins < n) return false
      update((s) => ({ ...s, economy: { ...s.economy, coins: s.economy.coins - n } }))
      return true
    },
    [update, value.economy.coins],
  )
  const spendGems = useCallback(
    (n: number) => {
      if (value.economy.gems < n) return false
      update((s) => ({ ...s, economy: { ...s.economy, gems: s.economy.gems - n } }))
      return true
    },
    [update, value.economy.gems],
  )
  const consumeLife = useCallback(() => {
    if (value.economy.lives <= 0) return false
    update((s) => ({
      ...s,
      economy: { ...s.economy, lives: Math.max(0, s.economy.lives - 1), livesUpdatedAt: Date.now() },
    }))
    return true
  }, [update, value.economy.lives])

  const ctx = useMemo<ProgressContextValue>(
    () => ({ save: value, loaded, update, addCoins, addGems, spendCoins, spendGems, consumeLife }),
    [value, loaded, update, addCoins, addGems, spendCoins, spendGems, consumeLife],
  )

  return <ProgressContext.Provider value={ctx}>{children}</ProgressContext.Provider>
}

export function useProgress(): ProgressContextValue {
  const ctx = useContext(ProgressContext)
  if (!ctx) throw new Error('useProgress must be used within <ProgressProvider>')
  return ctx
}

export { MAX_LIVES }
